/**
 * What close.ts cannot see from one shift alone: two of a person's registrations over each other,
 * or an end at or before its start. Either one keeps a shift out of every bulk approval in
 * Gjennomgang until it is corrected (manager redesign, 2026-10-09); rows.ts finds the same two
 * faults for Registreringer, so both tabs agree.
 */
import type { ApiShift } from '@/api/data';
import { overlapping } from '../arbeidstid/rows';
import { type Kind, approvable } from './close';

export type Block = 'overlap' | 'invalid';

export function blocksOf(shifts: ApiShift[]): Map<number, Block> {
  const out = new Map<number, Block>();
  const by = new Map<number, ApiShift[]>();
  for (const s of shifts) {
    if (s.user_id == null) continue;
    if (
      !s.is_approved &&
      s.punch_in_time &&
      s.punch_out_time &&
      Date.parse(s.punch_out_time) <= Date.parse(s.punch_in_time)
    )
      out.set(s.id, 'invalid');
    by.set(s.user_id, [...(by.get(s.user_id) ?? []), s]);
  }
  const approved = new Set(shifts.filter((s) => s.is_approved).map((s) => s.id));
  // an approved shift is settled; the one lying over it is what needs a person
  for (const mine of by.values())
    for (const id of overlapping(mine)) if (!out.has(id) && !approved.has(id)) out.set(id, 'overlap');
  return out;
}

/** A shift a person may tick for approval: ready or a deviation they looked at, never blocked. */
export const pickable = (k: Kind, block: Block | undefined) => approvable(k) && !block;
/** A shift "Se N klare" and "Velg godkjennbare" take: ready only, never blocked. */
export const bulkReady = (k: Kind, block: Block | undefined) => k === 'ready' && !block;

/**
 * The parts of a day split on a project switch («Bytt oppdrag», plan 0033 decision 8), from one
 * person's registrations on one day: the clocked ones in order when there are two or more and each
 * was clocked in at the second the one before was clocked out; else null. Two registrations that
 * merely share a day (an overlap, a second job later on) are not a split; a split may share its day
 * with such a row, which then stays a row of its own. Timer's day line and the
 * cards of «Uka» group by this one rule (#118).
 */
export function splitParts(rows: ApiShift[]): ApiShift[] | null {
  const v = rows
    .filter((s) => s.punch_in_time)
    .sort((a, b) => (a.punch_in_time ?? '').localeCompare(b.punch_in_time ?? ''));
  // the chain may share the day with a separate registration (a morning shift, then a switch in the
  // evening): the parts are the chained run, the other rows stay rows of their own
  // ponytail: one split per day, the longest run; two switches on separate jobs the same day show the longer
  let best: ApiShift[] = [];
  let run: ApiShift[] = [];
  for (const x of v) {
    const prev = run.at(-1);
    run =
      prev && Math.abs(Date.parse(x.punch_in_time ?? '') - Date.parse(prev.punch_out_time ?? '')) < 1000
        ? [...run, x]
        : [x];
    if (run.length > best.length) best = run;
  }
  return best.length > 1 ? best : null;
}
